import { reportPlaybackRejection } from '@/utils/media.utils'
import AudioLibrary from '@/webgl/audio/AudioLibrary'
import AudioMusic from '@/webgl/audio/AudioMusic'
import { PlayOptions } from '@/webgl/audio/AudioSource'
import AudioBundle from '@/webgl/audio/resources/AudioBundle'
import SubtitleLibrary from '@/webgl/audio/SubtitleLibrary'
import AssetDatabase from '@/webgl/resources/AssetDatabase'

import Sound from './Sound'

export function getAudioPath(url: string): string {
  return AssetDatabase.getAssetPath(url)
}

export function getSubtitlePath(url: string): string {
  return AssetDatabase.getAssetPath(url)
}

export default class AudioEngine {
  context: AudioContext
  master: GainNode
  ready: boolean = false
  initialized: boolean = false
  pendingInput: boolean = true

  library: AudioLibrary
  subtitles: SubtitleLibrary

  music: AudioMusic

  private static _instance: AudioEngine
  public static get instance(): AudioEngine {
    if (!AudioEngine._instance) {
      AudioEngine._instance = new AudioEngine()
    }
    return AudioEngine._instance
  }

  constructor() {
    const AudioContextClass =
      window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
    if (!AudioContextClass) {
      throw new Error(
        'AudioEngine: Web Audio API is unavailable — neither window.AudioContext nor window.webkitAudioContext is supported in this browser or environment'
      )
    }
    this.context = new AudioContextClass()

    document.addEventListener('visibilitychange', this.visibilityChange)

    this.master = this.context.createGain()
    this.master.gain.value = 1.0

    this.master.connect(this.context.destination)

    document.addEventListener('mousedown', () => {
      // iOS WebKit sits in the non-standard 'interrupted' state during audio-session
      // interruptions (incoming call, another app holding the device) — treat it like
      // 'suspended' so the gesture retry can recover.
      const state = this.context.state as AudioContextState | 'interrupted'
      if ((state === 'suspended' || state === 'interrupted') && this.pendingInput) {
        // console.log('[AudioEngine] Resuming audio context');
        this.pendingInput = false
        this.setContextState('resume', { rearmOnFailure: true })
      }
    })

    this.context.onstatechange = this.onStateChange
    this.onStateChange()

    this.library = new AudioLibrary()
    this.subtitles = new SubtitleLibrary()

    this.music = new AudioMusic(this)
  }

  static createBundle(): AudioBundle {
    return new AudioBundle(AudioEngine.instance)
  }

  static play(name: string, opts: PlayOptions = {}): Sound | null {
    const instance = AudioEngine.instance
    if (!instance.library.has(name)) {
      console.warn(`AudioEngine: No audio source with id %c%s`, 'color: white;', name)
      return null
    }

    const sound = instance.library.get(name).play(opts)
    instance.subtitles.update(name, sound)
    return sound
  }

  static pause() {
    AudioEngine.instance.setContextState('suspend')
  }

  static resume() {
    AudioEngine.instance.setContextState('resume')
  }

  static setMute(isMuted: boolean) {
    AudioEngine.instance.master.gain.value = isMuted ? 0 : 1
  }

  /**
   * Changes the audio context state without ever surfacing an unhandled rejection.
   *
   * WebKit rejects `resume()` with `InvalidStateError` or `NotAllowedError` whenever the OS
   * denies the audio session — an incoming call, an interrupted session, or another app holding
   * the device. The failure itself is benign (the context stays suspended), but left unhandled
   * the rejection escapes the web component and gets reported as a crash by the host page's
   * global `unhandledrejection` handler. When a gesture-driven resume is refused, the caller
   * opts into `rearmOnFailure` so the next user gesture retries instead of leaving audio dead
   * for the run — programmatic resumes (e.g. GameResume) must not re-arm, or a stray click
   * could resume audio while the game is deliberately paused.
   */
  private setContextState(action: 'resume' | 'suspend', { rearmOnFailure = false } = {}): void {
    const state = this.context.state
    const onFailure = (e: unknown) => {
      if (rearmOnFailure) this.pendingInput = true

      reportPlaybackRejection(e, {
        benignNames: ['InvalidStateError', 'NotAllowedError'],
        warning: `AudioEngine: audio context ${action} refused by the browser`,
        context: { feature: 'audio-context', action, state },
      })
    }

    try {
      // Older WebKit returns undefined here instead of a promise, hence the optional chain.
      ;(action === 'resume' ? this.context.resume() : this.context.suspend())?.catch(onFailure)
    } catch (e) {
      // WebKit can also throw synchronously instead of rejecting.
      onFailure(e)
    }
  }

  onStateChange = () => {
    this.ready = this.context.state === 'running'

    const initialized = this.initialized || this.ready

    if (initialized !== this.initialized && initialized) {
      this.music?.start()
    }

    this.initialized = initialized
  }

  visibilityChange = () => {
    if (!document.hidden && this.context.state !== 'running') {
      // console.log('[AudioEngine] came back with visibilityChange state:', this.context.state);
      this.setContextState('suspend')
      this.pendingInput = true
    }
  }
}
